import { formatPaymentLogDateTime } from "../../utils/applicationFormatters";
import { EditIconButton } from "@/components/ui/EditIconButton";
import { MentionTextarea } from "@/components/notes/MentionTextarea";
import { getNoteRowId, classifyNote } from "../../utils/notesTabHelpers";
import styles from "../../styles/applicationDetail.module.css";
import editStyles from "../../styles/editForm.module.css";
import type { CreditReportUnderwritingNotesSectionProps } from "../../types";
import { ApplicationNotesListEmpty } from "./ApplicationNotesListEmpty";

export function CreditReportUnderwritingNotesSection({
  tab,
}: CreditReportUnderwritingNotesSectionProps) {
  const {
    canShowEditUnderwriterNote,
    canViewUnderwritingNotesSection,
    filteredUnderwriterNotes,
    handleAddUnderwritingNote,
    setEditingUnderwriterNote,
    setUnderwritingNote,
    setUnderwritingTaggingContent,
    submittingNote,
    underwritingNote,
  } = tab;

  if (!canViewUnderwritingNotesSection) return null;

  return (
    <div className={`${styles.section} mt-8`} data-testid="application-credit-report-tab-section-underwriting-notes">
      <h2 className={styles.sectionTitle} data-testid="application-credit-report-tab-underwriting-notes-title">Underwriting Notes</h2>
      <div className={editStyles.formRow}>
        <div className={editStyles.formGroupFull}>
          <MentionTextarea
            className={editStyles.textarea}
            wrapperClassName={styles.addNoteMentionWrap}
            dataTestId="application-credit-report-tab-underwriting-note-input"
            placeholder="Add underwriting notes... (Use @ to mention users)"
            rows={4}
            value={underwritingNote}
            onChange={setUnderwritingNote}
            disabled={submittingNote}
            onTaggingAdd={(displayKey, useridToken) => {
              setUnderwritingTaggingContent((prev) => ({
                ...prev,
                [displayKey]: useridToken,
              }));
            }}
            style={{
              width: "100%",
              boxSizing: "border-box",
              resize: "vertical",
              minHeight: "100px",
            }}
          />
          <div style={{ marginTop: "10px" }}>
            <button
              type="button"
              className={styles.btnSmall}
              data-testid="application-credit-report-tab-add-underwriting-note-button"
              onClick={handleAddUnderwritingNote}
              disabled={submittingNote || !underwritingNote.trim()}
            >
              {submittingNote ? "Submitting..." : "Submit"}
            </button>
          </div>
        </div>
      </div>
      <div
        className="mt-6 border-t border-slate-200/80 pt-5"
        role="region"
        aria-label="Underwriting recent notes"
      >
        {filteredUnderwriterNotes.length > 0 ? (
          <>
            <h6
              className={styles.sectionTitle}
              style={{ paddingBottom: "8px" }}
            >
              Recent Notes
            </h6>
            <div
              className={styles.notesList}
              style={{
                background: "#fafafa",
                padding: 0,
                borderRadius: "4px",
                border: "1px solid #eee",
                overflow: "hidden",
              }}
            >
              {filteredUnderwriterNotes.map((note, index) => {
                const kind = classifyNote(note);
                const isSystem = kind === "system";
                const body =
                  (note.notes as string | undefined) ??
                  (note.note as string | undefined) ??
                  (note.note_text as string | undefined) ??
                  "";
                const author =
                  (note.name as string | undefined) ??
                  (note.username as string | undefined) ??
                  (note.created_by as string | undefined) ??
                  (note.user_name as string | undefined) ??
                  "System";
                return (
                  <div
                    key={String(getNoteRowId(note) ?? `uw-${index}`)}
                    data-testid={`application-credit-report-tab-uw-note-row-${getNoteRowId(note) ?? index}`}
                    className={styles.noteItem}
                    style={{
                      display: "flex",
                      borderBottom:
                        index < filteredUnderwriterNotes.length - 1
                          ? "1px solid #ececec"
                          : "none",
                      padding: "14px 16px",
                      background: index % 2 === 0 ? "#ffffff" : "#f7f8fa",
                      borderLeft: `4px solid ${
                        isSystem ? "#94a3b8" : "#3b82f6"
                      }`,
                    }}
                  >
                    <div
                      style={{
                        width: "200px",
                        borderRight: "1px solid #ececec",
                        paddingRight: "14px",
                        flexShrink: 0,
                      }}
                    >
                      <strong
                        style={{
                          display: "block",
                          color: "#111827",
                          fontSize: "13px",
                          lineHeight: 1.35,
                        }}
                      >
                        {author}
                        {note.impersonated_by
                          ? ` (impersonated by ${String(note.impersonated_by)})`
                          : ""}
                      </strong>
                      <span
                        style={{
                          display: "block",
                          fontSize: "12px",
                          color: "#6b7280",
                          marginTop: "2px",
                        }}
                      >
                        {formatPaymentLogDateTime(
                          note.created_at as string | number | undefined,
                        )}
                      </span>
                      {note.updated_at ? (
                        <p
                          style={{
                            margin: "4px 0 0",
                            fontSize: "11px",
                            color: "#dc2626",
                          }}
                        >
                          Modified{" "}
                          {formatPaymentLogDateTime(
                            note.updated_at as string | number | undefined,
                          )}
                        </p>
                      ) : null}
                    </div>
                    <div
                      style={{
                        flex: 1,
                        paddingLeft: "20px",
                        fontSize: "13px",
                        lineHeight: 1.55,
                        color: "#1f2937",
                        wordBreak: "break-word",
                      }}
                    >
                      {isSystem ? (
                        <span
                          className={styles.notesContent}
                          dangerouslySetInnerHTML={{ __html: body }}
                        />
                      ) : (
                        <span
                          className={styles.notesContent}
                          style={{ whiteSpace: "pre-wrap" }}
                        >
                          {body}
                        </span>
                      )}
                      {canShowEditUnderwriterNote(note) ? (
                        <EditIconButton
                          onClick={() => setEditingUnderwriterNote(note)}
                          className="ml-3 mt-2 align-middle"
                          style={{
                            cursor: "pointer",
                          }}
                          title="Edit note"
                          aria-label="Edit note"
                          data-testid={`application-credit-report-tab-uw-edit-button-${getNoteRowId(note)}`}
                        />
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <ApplicationNotesListEmpty testId="application-credit-report-tab-uw-empty-notes" />
        )}
      </div>
    </div>
  );
}
